import { assign, fromPromise, setup } from 'xstate';

import type {
  ActivateAccountResponse,
  OnboardingStateResponseRead,
  PostMyHighlighterAccountsAcceptTosApiResponse,
} from '@highlight-cp/highlighter-api';

import { STEP } from './constants';
import {
  hasApplicationInProgress,
  hasApplicationInReview,
  isActive,
  isGuest,
  needsCoreProfile,
  needsDetailsAtAddress,
  needsDetailsAtDob,
  needsDetailsAtEmail,
  needsDetailsAtPhone,
  needsTosAgreement,
  readyToActivate,
  requiresManualActivation,
} from './guards';
import type { OnboardingContext, OnboardingEvent } from './types';

export const onboardingMachine = setup({
  types: {} as {
    context: OnboardingContext;
    events: OnboardingEvent;
  },
  actors: {
    fetchOnboardingState: fromPromise<OnboardingStateResponseRead, void>(async () => {
      throw new Error('not implemented');
    }),
    activateAccount: fromPromise<ActivateAccountResponse, void>(async () => {
      throw new Error('not implemented');
    }),
    acceptTos: fromPromise<PostMyHighlighterAccountsAcceptTosApiResponse, void>(async () => {
      throw new Error('not implemented');
    }),
  },
  actions: {
    storeError: assign({
      error: ({ event }) => (event as unknown as { error: unknown }).error,
    }),
    logUnknownOnboardingState: () => {},
  },
}).createMachine({
  id: 'onboarding',
  initial: 'loading',
  context: {
    data: null,
    error: null,
  },
  states: {
    /**
     * Fetching the current onboarding state from the server.
     * Guards on the response determine which state to transition to next.
     */
    loading: {
      invoke: {
        src: 'fetchOnboardingState',
        onDone: [
          // NOTE: These guards run sequentially; updating the order of them could have an effect on the order of steps
          // Specifically `needsDetails` steps will change order in the UI if the order is changed in this list
          {
            guard: ({ event }) => isActive(event.output),
            target: 'activated',
            actions: assign({ data: ({ event }) => event.output, error: null }),
          },
          {
            guard: ({ event }) => needsTosAgreement(event.output),
            target: 'tosAgreement',
            actions: assign({ data: ({ event }) => event.output, error: null }),
          },
          {
            guard: ({ event }) => isGuest(event.output),
            target: 'activated',
            actions: assign({ data: ({ event }) => event.output, error: null }),
          },
          {
            guard: ({ event }) => needsDetailsAtPhone(event.output),
            target: 'welcome',
            actions: assign({ data: ({ event }) => event.output, error: null }),
          },
          {
            guard: ({ event }) => needsDetailsAtDob(event.output),
            target: 'collectingDetails.dob',
            actions: assign({ data: ({ event }) => event.output, error: null }),
          },
          {
            guard: ({ event }) => needsDetailsAtAddress(event.output),
            target: 'collectingDetails.address',
            actions: assign({ data: ({ event }) => event.output, error: null }),
          },
          {
            guard: ({ event }) => needsDetailsAtEmail(event.output),
            target: 'collectingDetails.email',
            actions: assign({ data: ({ event }) => event.output, error: null }),
          },
          {
            guard: ({ event }) => hasApplicationInProgress(event.output),
            target: 'completingApplicationSurvey',
            actions: assign({ data: ({ event }) => event.output, error: null }),
          },
          {
            guard: ({ event }) => needsCoreProfile(event.output),
            target: 'completingCoreProfile',
            actions: assign({ data: ({ event }) => event.output, error: null }),
          },
          {
            guard: ({ event }) => requiresManualActivation(event.output),
            target: 'pendingActivation',
            actions: assign({ data: ({ event }) => event.output, error: null }),
          },
          {
            guard: ({ event }) => readyToActivate(event.output),
            target: 'activating',
            actions: assign({ data: ({ event }) => event.output, error: null }),
          },
          {
            guard: ({ event }) => hasApplicationInReview(event.output),
            target: 'awaitingStaffReview',
            actions: assign({ data: ({ event }) => event.output, error: null }),
          },
          {
            target: 'unknown',
            actions: [
              assign({
                data: ({ event }) => event.output,
                error: 'Unexpected onboarding state: no guard matched.',
              }),
              'logUnknownOnboardingState',
            ],
          },
        ],
        onError: {
          target: 'error',
          actions: 'storeError',
        },
      },
    },

    /**
     * Participation agreement gate shown once when TOS has not been accepted.
     */
    tosAgreement: {
      on: { AGREE_TO_TOS: 'acceptingTos' },
    },

    /**
     * Persist TOS acceptance, then reload onboarding state from the server.
     */
    acceptingTos: {
      invoke: {
        src: 'acceptTos',
        onDone: { target: 'loading' },
        onError: { target: 'error', actions: 'storeError' },
      },
    },

    /**
     * Welcome screen shown before collecting personal details.
     * Phone has not yet been verified — the user must acknowledge before proceeding to the details form.
     */
    welcome: { on: { LETS_GO: 'collectingDetails.phone' } },

    /**
     * Multi-step form collecting the highlighter's personal details.
     * Sub-steps: phone → name → dob → address → email.
     * Completing email triggers a server reload to re-evaluate onboarding state.
     */
    collectingDetails: {
      initial: STEP.PHONE,
      states: {
        phone: {
          on: { NEXT: STEP.NAME },
        },
        name: {
          on: { NEXT: STEP.DOB, BACK: STEP.PHONE },
        },
        dob: {
          on: { NEXT: STEP.ADDRESS, BACK: STEP.NAME },
        },
        address: {
          on: { NEXT: STEP.EMAIL, BACK: STEP.DOB },
        },
        email: {
          on: { NEXT: '#onboarding.loading', BACK: STEP.ADDRESS },
        },
      },
      on: { WELCOME: 'welcome' },
    },

    /**
     * The highlighter has a pending application survey (status: sent or started) that must be completed.
     * Shows an intro prompt before launching the survey.
     */
    completingApplicationSurvey: {
      initial: 'intro',
      states: {
        intro: { on: { CONTINUE: 'applicationSurvey' } },
        applicationSurvey: { on: { SURVEY_COMPLETED: '#onboarding.loading' } },
      },
    },

    /**
     * Application submitted and currently under staff review; the highlighter has no core profile surveys yet.
     * Shows a "sit tight" waiting card. Non-core-profile bonus surveys may still be available.
     */
    awaitingStaffReview: {
      initial: 'waitingCard',
      states: {
        waitingCard: { on: { CONTINUE: 'additionalSurvey' } },
        additionalSurvey: { on: { SURVEY_COMPLETED: '#onboarding.loading' } },
      },
    },

    /**
     * The highlighter has core profile surveys to complete.
     * Covers users with no application survey and those whose application is in a post-submission state.
     * Shows an intro prompt before launching the core profile survey.
     */
    completingCoreProfile: {
      initial: 'intro',
      states: {
        intro: { on: { CONTINUE: 'coreProfileSurvey' } },
        coreProfileSurvey: {
          on: {
            SURVEY_COMPLETED: { target: '#onboarding.loading' },
          },
        },
      },
    },

    /**
     * All prerequisites met — invoking the activate-account API call.
     * Transitions to activated on success or error on failure.
     */
    activating: {
      invoke: {
        src: 'activateAccount',
        onDone: { target: 'activated' },
        onError: { target: 'error', actions: 'storeError' },
      },
    },

    /**
     * All prerequisites are met but the community does not auto-activate.
     * The highlighter remains in "new" status until staff manually activates them.
     */
    pendingActivation: { type: 'final' },

    /**
     * Fallback when onboarding state cannot be mapped to known guard rules.
     * Allows user to retry while we keep telemetry for investigation.
     */
    unknown: {
      on: {
        RETRY: 'loading',
      },
    },

    /**
     * Account successfully activated. Terminal state — no further transitions.
     */
    activated: { type: 'final' },

    /**
     * A loading or activation error occurred.
     * The user can send RETRY to re-enter the loading state and try again.
     */
    error: {
      on: {
        RETRY: 'loading',
      },
    },
  },
});
